// ===========================================================================
// One behaviour for every panel that opens over the app.
//
// There were four of them (the shop, the account panel, regulars, and a
// community invitation) and between them they had: no backdrop, so nothing
// dimmed or blocked the page behind and every link back there stayed in the
// tab order; two with `role="dialog"` and two with nothing at all; none with
// `aria-modal`; and, in all four, no Escape, no initial focus, no focus trap,
// and no focus restore. The only way out of any of them was a Close button
// measuring 73 x 33 on a phone.
//
// This is not a component: the four panels keep their own markup and their
// own look. It takes an element that is already built and gives it the
// behaviour a dialog is supposed to have.
//
// ASCII only.
// ===========================================================================

import { el } from '../dom.js';

/** Everything focusable inside a panel, in tab order. */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableIn(root) {
  return [...root.querySelectorAll(FOCUSABLE)].filter((e) => e.offsetParent !== null || e === document.activeElement);
}

/**
 * Make `panel` behave like a dialog, and return a function that closes it.
 *
 * @param {HTMLElement} panel   already built and already in the document
 * @param {object} [opts]
 * @param {string} [opts.label] what the dialog is, for a screen reader
 * @param {() => void} [opts.onClose] run after it closes, for the caller's own cleanup
 * @returns {{ close: () => void }}
 */
export function asModal(panel, { label = 'Dialog', onClose = null } = {}) {
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  if (!panel.getAttribute('aria-label')) panel.setAttribute('aria-label', label);

  // The page behind is dimmed AND unreachable. A backdrop is not decoration:
  // without one, a tap that misses the panel lands on whatever is behind it.
  const backdrop = el('div', { class: 'modal-backdrop' });
  panel.parentNode?.insertBefore(backdrop, panel);

  // Where focus came from, so it can be put back. Losing your place in the
  // page is the part of a bad dialog that is hardest to recover from.
  const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

  let closed = false;
  function close() {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKeydown, true);
    backdrop.remove();
    panel.remove();
    // Back where they were, if it is still on the page.
    if (opener && opener.isConnected) opener.focus();
    onClose?.();
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    // The trap. Tab from the last focusable goes to the first, and
    // shift-Tab from the first goes to the last, so focus cannot walk out
    // of the dialog into the page it is covering.
    const items = focusableIn(panel);
    if (!items.length) {
      e.preventDefault();
      panel.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (!panel.contains(active)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  backdrop.addEventListener('click', close);
  document.addEventListener('keydown', onKeydown, true);

  // Focus goes INTO the dialog, on the first thing worth acting on, or the
  // panel itself when there is nothing yet (a panel that loads its content).
  const first = focusableIn(panel)[0];
  if (first) {
    first.focus();
  } else {
    panel.tabIndex = -1;
    panel.focus();
  }

  return { close };
}

export default { asModal };
